/**
 * The arcade announcer.
 *
 * We cannot ship recorded speech (N1: no external assets), so the announcer is a FORMANT
 * SYNTHESISER. A sawtooth glottal source plus a little breath noise runs through four
 * parallel bandpass filters tuned to vowel formants; a per-syllable pitch contour and
 * amplitude envelope supply the rhythm; a waveshaper and a 230–3900 Hz band-limit make it a
 * tannoy rather than a synth pad.
 *
 * It is NOT intelligible speech and it is not trying to be. It is a bark with the right
 * cadence and attitude, sitting under the text the UI is already showing. What carries the
 * meaning is the rhythm and the pitch shape: "PER-fect!" rises, "GAME o-ver" falls,
 * "COM-BO" is two even stressed beats that climb a fourth.
 *
 * [MEASURED] the reference announcer sat on stable formants at 657–678 / 894 / ~1766 Hz —
 * a back-vowel male register. AUDIO_TUNING.announcer.vowels.aw is pinned to those numbers
 * and every other vowel is placed relative to it.
 *
 * Nodes: one persistent chain. A line is a schedule of AudioParam automation, not a graph
 * rebuild — announcing costs zero allocations on the audio thread.
 */

import { AUDIO_TUNING as T } from './tuning.js';
import { makeDriveCurve } from './dsp.js';

const A = T.announcer;

/** Resolve a line id, including the `comboN` family and the alias table. */
export function resolveLine(id) {
  if (!id) return null;
  if (A.lines[id]) return { key: id, tier: 0 };
  const alias = A.alias[id];
  if (alias && A.lines[alias]) {
    const m = /^combo(\d+)$/.exec(id);
    return { key: alias, tier: m ? Math.min(A.comboTierMax, Number(m[1])) : 0 };
  }
  const m = /^combo(\d+)$/i.exec(id);
  if (m) return { key: 'combo', tier: Math.min(A.comboTierMax, Number(m[1])) };
  const lower = String(id).toLowerCase();
  for (const k of Object.keys(A.lines)) if (k.toLowerCase() === lower) return { key: k, tier: 0 };
  return { key: 'combo', tier: 0 };
}

/** Total speaking time of a line in seconds, including inter-syllable gaps. */
export function lineDuration(key) {
  const line = A.lines[key];
  if (!line) return 0.4;
  let d = 0;
  for (const s of line.syl) d += s.d + A.gap;
  return d + A.release;
}

export class Announcer {
  constructor(graph, onDuck) {
    this.graph = graph;
    this.onDuck = onDuck || (() => {});
    this.built = false;

    /** Pending lines: {key, tier, priority}. Bounded by A.maxQueue. */
    this.queue = [];
    this.current = null;
    /** Audio-clock time at which the current line finishes. */
    this.busyUntil = 0;
    /** Model clock, advanced by update(dt). Used when there is no audio context at all. */
    this.clock = 0;
    this.spokenCount = 0;
    this.droppedCount = 0;
    this.lastLine = null;
    this.enabled = true;
  }

  build() {
    const g = this.graph;
    if (!g.live || this.built) return;
    const a = g.actx;
    const now = a.currentTime;

    this.out = a.createGain();
    this.out.gain.value = 1;
    this.out.connect(g.bus.voice);

    // ---- band-limit + grit: this is what turns "synth" into "PA speaker"
    this.postLp = a.createBiquadFilter();
    this.postLp.type = 'lowpass';
    this.postLp.frequency.value = A.postLpHz;
    this.postLp.Q.value = 0.8;
    this.postLp.connect(this.out);

    this.shaper = a.createWaveShaper();
    this.shaper.curve = makeDriveCurve(A.driveAmount);
    this.shaper.oversample = '2x';
    this.shaper.connect(this.postLp);

    this.postHp = a.createBiquadFilter();
    this.postHp.type = 'highpass';
    this.postHp.frequency.value = A.postHpHz;
    this.postHp.Q.value = 0.7;
    this.postHp.connect(this.shaper);

    // ---- the gate: one gain the syllable envelope writes to
    this.gate = a.createGain();
    this.gate.gain.value = 0.0001;
    this.gate.connect(this.postHp);

    // ---- formant bank
    this.formants = [];
    for (let i = 0; i < 4; i++) {
      const bp = a.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = i === 3 ? A.f4 : [700, 1100, 2400][i];
      bp.Q.value = A.formantQ[i];
      const fg = a.createGain();
      fg.gain.value = A.formantGain[i];
      bp.connect(fg);
      fg.connect(this.gate);
      this.formants.push({ bp, g: fg });
    }

    // ---- glottal source
    this.buzz = a.createOscillator();
    this.buzz.type = 'sawtooth';
    this.buzz.frequency.value = A.basePitch;
    this.buzzGain = a.createGain();
    this.buzzGain.gain.value = A.buzzGain;
    this.buzz.connect(this.buzzGain);
    this.buzz.start(now);

    // vibrato
    this.vib = a.createOscillator();
    this.vib.type = 'sine';
    this.vib.frequency.value = A.vibratoHz;
    this.vibDepth = a.createGain();
    this.vibDepth.gain.value = A.vibratoCents;
    this.vib.connect(this.vibDepth);
    this.vibDepth.connect(this.buzz.detune);
    this.vib.start(now);

    // breath
    this.breath = g.makeNoiseSource('white');
    this.breathGain = a.createGain();
    this.breathGain.gain.value = A.breathGain;
    this.breath.connect(this.breathGain);

    for (const f of this.formants) {
      this.buzzGain.connect(f.bp);
      this.breathGain.connect(f.bp);
    }

    // ---- consonants: a separate noise path that bypasses the formant bank, so a plosive
    //      is a burst of air rather than a filtered vowel.
    this.consSrc = g.makeNoiseSource('white');
    this.consSrc.playbackRate.value = 1.31;
    this.consHp = a.createBiquadFilter();
    this.consHp.type = 'highpass';
    this.consHp.frequency.value = A.plosive.hp;
    this.consGain = a.createGain();
    this.consGain.gain.value = 0.0001;
    this.consSrc.connect(this.consHp); this.consHp.connect(this.consGain);
    this.consGain.connect(this.postHp);

    this.built = true;
  }

  /**
   * Queue a bark. Higher priority interrupts a lower-priority line that is already playing;
   * equal or lower priority queues behind it (bounded), and anything that does not fit is
   * dropped rather than delayed into irrelevance.
   */
  say(id, priority = 1) {
    const r = resolveLine(id);
    if (!r) return false;
    const item = { key: r.key, tier: r.tier, priority, id };
    this.lastLine = id;

    const busy = this.isSpeaking();
    if (busy && priority > this.current.priority) {
      this._cutOff();
      this.queue.length = 0;
      this.queue.push(item);
      this._pump();
      return true;
    }
    if (priority < A.dropBelow && busy) { this.droppedCount++; return false; }
    if (this.queue.length >= A.maxQueue) {
      // Replace the lowest-priority queued item if this one beats it; else drop.
      let worst = 0;
      for (let i = 1; i < this.queue.length; i++) if (this.queue[i].priority < this.queue[worst].priority) worst = i;
      if (this.queue[worst].priority < priority) this.queue[worst] = item;
      else this.droppedCount++;
      return false;
    }
    this.queue.push(item);
    this._pump();
    return true;
  }

  isSpeaking() { return !!this.current && this._now() < this.busyUntil; }

  _now() { return this.graph.live ? this.graph.actx.currentTime : this.clock; }

  _cutOff() {
    this.current = null;
    this.busyUntil = this._now();
    if (!this.graph.live || !this.built) return;
    const t = this.graph.actx.currentTime;
    this.gate.gain.cancelScheduledValues(t);
    this.gate.gain.setValueAtTime(Math.max(0.0001, this.gate.gain.value), t);
    this.gate.gain.linearRampToValueAtTime(0.0001, t + A.interruptFade);
  }

  /** Start the next queued line if nothing is speaking. */
  _pump() {
    if (this.isSpeaking() || this.queue.length === 0) return;
    const item = this.queue.shift();
    const start = Math.max(this._now(), this.busyUntil) + A.queueGap;
    const dur = this._schedule(item, start);
    this.current = item;
    this.busyUntil = start + dur;
    this.spokenCount++;
    this.onDuck(dur + T.duck.voiceTail, T.duck.voiceAmount);
  }

  /**
   * Write the whole line into the AudioParam timeline in one pass. Returns its duration.
   * When there is no audio context this still returns the duration, so the queue drains at
   * the correct rate in the harness and harnessStats stays honest.
   */
  _schedule(item, start) {
    const line = T.announcer.lines[item.key];
    if (!line) return 0.3;

    const tierSemis = item.tier > 1 ? (item.tier - 2) * A.comboTierSemitones : 0;
    const base = A.basePitch * line.pitch * Math.pow(2, tierSemis / 12);
    const gain = (line.gain || 1) * A.gain;

    let t = start;
    let total = 0;
    const live = this.graph.live && this.built && this.enabled;

    if (live) {
      const gate = this.gate.gain;
      gate.cancelScheduledValues(start);
      gate.setValueAtTime(0.0001, start);
      this.consGain.gain.cancelScheduledValues(start);
      this.consGain.gain.setValueAtTime(0.0001, start);
    }

    for (let i = 0; i < line.syl.length; i++) {
      const s = line.syl[i];
      const f = A.vowels[s.v] || A.vowels.aw;
      const p0 = base * Math.pow(2, (s.p || 0) / 12);
      const p1 = base * Math.pow(2, ((s.p || 0) + (s.bend || 0)) / 12);
      const amp = Math.max(0.02, gain * (s.g == null ? 1 : s.g));

      if (live) {
        // formants: step at syllable onset, then hold (a glide here smears the consonant)
        for (let k = 0; k < 3; k++) {
          this.formants[k].bp.frequency.setValueAtTime(f[k], t);
        }
        this.formants[3].bp.frequency.setValueAtTime(A.f4, t);

        // pitch contour
        this.buzz.frequency.setValueAtTime(p0, t);
        if (p1 !== p0) this.buzz.frequency.exponentialRampToValueAtTime(Math.max(20, p1), t + s.d);

        // consonant onset
        if (s.c === 'p') {
          const cg = this.consGain.gain;
          this.consHp.frequency.setValueAtTime(A.plosive.hp, t);
          cg.setValueAtTime(0.0001, Math.max(0, t - 0.006));
          cg.linearRampToValueAtTime(A.plosive.gain * amp, t);
          cg.setTargetAtTime(0.0001, t, A.plosive.decay);
        } else if (s.c === 's') {
          const cg = this.consGain.gain;
          this.consHp.frequency.setValueAtTime(A.sibilant.hp, Math.max(0, t - A.sibilant.dur));
          cg.setValueAtTime(0.0001, Math.max(0, t - A.sibilant.dur));
          cg.linearRampToValueAtTime(A.sibilant.gain * amp, t - A.sibilant.dur * 0.3);
          cg.linearRampToValueAtTime(0.0001, t + 0.01);
          this.consHp.frequency.setValueAtTime(A.plosive.hp, t + 0.02);
        }

        // vowel envelope
        const gate = this.gate.gain;
        gate.setValueAtTime(0.0001, t);
        gate.linearRampToValueAtTime(amp, t + A.attack);
        gate.setValueAtTime(amp, t + s.d - A.release);
        gate.linearRampToValueAtTime(0.0001, t + s.d);
      }

      t += s.d + A.gap;
      total += s.d + A.gap;
    }
    return total + A.release;
  }

  /** Presentation step. Drains the queue and advances the fallback clock. */
  update(dt) {
    if (!this.graph.live) this.clock += dt;
    if (!this.isSpeaking()) {
      this.current = null;
      if (this.queue.length) this._pump();
    }
  }

  setEnabled(on) {
    this.enabled = on;
    if (this.graph.live && this.built) this.graph.param(this.out.gain, on ? 1 : 0.0001, 0.04);
  }

  reset() {
    this.queue.length = 0;
    this.current = null;
    this.busyUntil = 0;
    this.clock = 0;
    this.spokenCount = 0;
    this.droppedCount = 0;
    this.lastLine = null;
    if (this.graph.live && this.built) this._cutOff();
  }

  stats() {
    return {
      speaking: this.isSpeaking(),
      queued: this.queue.length,
      spoken: this.spokenCount,
      dropped: this.droppedCount,
      last: this.lastLine,
    };
  }
}
